import React, {useCallback, useContext, useEffect, useState} from 'react';

import {FormatSettingsSection} from 'src/components/format-settings';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useNewsletterDictionaryForUser} from 'src/hooks/dictionaries/useNewsletterDictionaryForUser';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    PbWorkspaceSettingsSection, useWorkspaceSettings,
} from 'src/modules/profile/account/create-user/steps/users-settings/components/workspace-settings';
import {useFormatSettings} from 'src/modules/profile/user/tabs/personalization/view-prefernces/format-settings/useFormatSettings';

import {BrowserLimitsSection, useUserBrowserLimits} from './components/browser-limits-configuration';
import {ImportSettings, useTemplateSettings} from './components/imports-settings';
import {NewslettersSettings, useUserNewsletter} from './components/newsletters-settings';
import {
    PbEditorialSettingsSection,
    useAnalystSettings,
    useMarketMapsSettings,
} from './components/pb-editorial-settings';

import type {MessageContextType} from 'src/contexts/message-context';
import type {Error} from 'src/openapi/admin/models/Error';

type UserSettingsContent = {
    content: React.ReactNode,
    isChunkChanged: boolean,
    busy: boolean,
    loadingError: Error | null,
    onSave: () => void,
};

type Props = {
    userId: number,
    onProceed: () => void,
    children: (content: UserSettingsContent) => React.ReactElement,
};

export const UserSettingsController = ({userId, children, onProceed}: Props) => {
    const [busy, setBusy] = useState<boolean>(false);
    const {userFieldChange} = useEventTracking();

    const {setErrorMessage} = useContext<MessageContextType>(MessageContext);
    const {setIsChunkChanged, isCurrentChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'user-settings',
        onProceed,
    });

    const onFieldChange = (section: string) => (parameterName: string) => {
        userFieldChange({
            parameterName,
            section,
            viewType: 'New',
        });
    };

    const {
        isLoading: isUserNewsletterLoading,
        loadingError: newsletterLoadingError,
        isChanged: isChangedUserNewsletter,

        data: newsletterTypeCodes,
        setData: setNewsletterTypeCodes,

        saveChanges: saveUserNewsletter,
    } = useUserNewsletter({userId});

    const {fullNewsletterDictionary, availableNewsletterTypes} = useNewsletterDictionaryForUser({userId});

    const {
        emailAlerts, setEmailAlerts,
        templates, setTemplates,
        savedSearches, setSavedSearches,

        selectedTemplateUserId, setSelectedTemplateUserId,

        onSave: onSaveTemplateSettings,
        isChanged: isTemplateSettingsChanged,
    } = useTemplateSettings({userId});

    const {
        loadingError: localeLoadingError,
        isLoading: isLocaleLoading,
        data: locale,
        dataInitial: defaultLocale,
        setData: onChangeLocale,
        isChanged: isLocaleChanged,
        saveChanges: onLocaleSave,
    } = useFormatSettings({userId});

    const {
        isLoading: isBrowserLimitsLoading,
        loadingError: browserLimitsLoadingError,
        data: browserLimits,
        setData: setBrowserLimits,
        isChanged: isBrowserLimitsChanged,
        saveChanges: onSaveBrowserRegistrations,
    } = useUserBrowserLimits({userId});

    const {
        isLoading: isAnalystSettingsLoading,
        loadingError: analystSettingsLoadingError,
        isChanged: isAnalystSettingsChanged,
        data: analystSettings,
        setData: setAnalystSettings,
        saveChanges: saveAnalystSettings,
    } = useAnalystSettings({userId});

    const {
        isLoading: isWorkspaceSettingsLoading,
        loadingError: workspaceSettingsLoadingError,
        isChanged: isWorkspaceSettingsChanged,
        data: workspaceSettings,
        setData: setWorkspaceSettings,
        saveChanges: saveWorkspaceSettings,
    } = useWorkspaceSettings({userId});

    const {
        isLoading: isMarketMapsSettingsLoading,
        loadingError: marketMapsSettingsLoadingError,
        isChanged: isMarketMapsSettingsChanged,
        data: marketMapsSettings,
        setData: setMarketMapsSettings,
        saveChanges: saveMarketMapsSettings,
    } = useMarketMapsSettings({userId});

    useEffect(() => {
        setIsChunkChanged(
            isChangedUserNewsletter ||
            isTemplateSettingsChanged ||
            isLocaleChanged ||
            isBrowserLimitsChanged ||
            isAnalystSettingsChanged ||
            isWorkspaceSettingsChanged ||
            isMarketMapsSettingsChanged,
        );
    }, [
        setIsChunkChanged,
        isChangedUserNewsletter,
        isTemplateSettingsChanged,
        isLocaleChanged,
        isBrowserLimitsChanged,
        isAnalystSettingsChanged,
        isMarketMapsSettingsChanged,
        isWorkspaceSettingsChanged,
    ]);

    const onSave = useCallback(async () => {
        const calls = [];

        setBusy(true);
        calls.push(await onSaveTemplateSettings());

        if (isChangedUserNewsletter) {
            calls.push(await saveUserNewsletter());
        }

        if (isLocaleChanged) {
            calls.push(await onLocaleSave());
        }

        if (isBrowserLimitsChanged) {
            calls.push(await onSaveBrowserRegistrations());
        }

        if (isWorkspaceSettingsChanged) {
            calls.push(await saveWorkspaceSettings());
        }

        if (isAnalystSettingsChanged) {
            calls.push(await saveAnalystSettings());
        }

        if (isMarketMapsSettingsChanged) {
            calls.push(await saveMarketMapsSettings());
        }

        setBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];

        if (errors.length) {
            setErrorMessage(errors.join(' '));
        } else {
            proceed();
        }
    }, [
        isChangedUserNewsletter,
        isLocaleChanged,
        isWorkspaceSettingsChanged,
        isBrowserLimitsChanged,
        isAnalystSettingsChanged,
        isMarketMapsSettingsChanged,
        onSaveTemplateSettings,
        saveUserNewsletter,
        onLocaleSave,
        onSaveBrowserRegistrations,
        saveMarketMapsSettings,
        saveWorkspaceSettings,
        saveAnalystSettings,
        proceed,
        setErrorMessage,
    ]);

    const loadingError = newsletterLoadingError || localeLoadingError;

    return children({
        content: [
            <NewslettersSettings
                key="NewslettersSettings"
                {...{
                    isLoading: isUserNewsletterLoading,
                    loadingError: newsletterLoadingError,
                    newsletterTypeCodes,
                    setNewsletterTypeCodes,
                    onFieldChange: onFieldChange('Newsletters'),
                    newsletterDictionary: fullNewsletterDictionary,
                    availableNewsletterTypes,
                }}
            />,
            <ImportSettings
                key="ImportOptions"
                {...{
                    setSelectedTemplateUserId,
                    savedSearches,
                    setEmailAlerts,
                    emailAlerts,
                    templates,
                    setTemplates,
                    selectedTemplateUserId,
                    setSavedSearches,
                    onFieldChange: onFieldChange('Import options'),
                }}
            />,
            <FormatSettingsSection
                key="FormatSettingsSection"
                isLoading={isLocaleLoading}
                loadingError={localeLoadingError}
                onChange={onChangeLocale}
                locale={locale}
                defaultLocale={defaultLocale}
                onFieldChange={onFieldChange('Format settings')}
                withTimeZone={true}
            />,
            <BrowserLimitsSection
                key="BrowserLimitsComponent"
                isLoading={isBrowserLimitsLoading}
                loadingError={browserLimitsLoadingError}
                browserLimits={browserLimits}
                setBrowserLimits={setBrowserLimits}
                onFieldChange={onFieldChange('Browser limits')}
            />,
            <PbEditorialSettingsSection
                key="PbEditorialSettingsSection"
                isLoading={isAnalystSettingsLoading || isMarketMapsSettingsLoading}
                loadingError={analystSettingsLoadingError || marketMapsSettingsLoadingError}
                analystSettings={analystSettings}
                marketMapsSettings={marketMapsSettings}
                setAnalystSettings={setAnalystSettings}
                setMarketMapsSettings={setMarketMapsSettings}
                onFieldChange={onFieldChange('Editorial settings')}
            />,
            <PbWorkspaceSettingsSection
                key="PbWorkspaceSettingsSection"
                isLoading={isWorkspaceSettingsLoading}
                loadingError={workspaceSettingsLoadingError}
                workspaceSettings={workspaceSettings}
                setWorkspaceSettings={setWorkspaceSettings}
                onFieldChange={onFieldChange('Workspace settings')}
            />,
        ],
        isChunkChanged: isCurrentChunkChanged,
        busy,
        onSave,
        loadingError,
    });
};
